import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { http } from '../../../lib/api';
import { AppLayout } from '../../../components/layout';
import { Button, Card, Badge, SpinnerBlock, Avatar } from '../../../components/core';
import { NAV } from '../nav';
import { subscribeRealtimeEvents } from '../../../lib/ws';
import type { ConversationDto, MessageDto, UserRole } from '@livestreamx/shared-types';

interface MessagesResponse {
  items: MessageDto[];
  total: number;
  page: number;
  pageSize: number;
}

export default function TenantChatConversation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ConversationDto | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadConversation = async () => {
    if (!id) return;
    try {
      const conv = await http.get<ConversationDto>(`/api/chat/tenant/conversations/${id}`);
      setConversation(conv);
    } catch {
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (pageNum = 1, append = false) => {
    if (!id) return;
    try {
      const res = await http.get<MessagesResponse>(`/api/chat/tenant/conversations/${id}/messages?page=${pageNum}&pageSize=50`);
      if (append) {
        setMessages((prev) => [...res.items.reverse(), ...prev]);
      } else {
        setMessages(res.items);
      }
      setHasMore(res.items.length === 50);
      setPage(pageNum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    loadConversation();
    loadMessages();
    scrollToBottom();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = subscribeRealtimeEvents((event: unknown) => {
      const e = event as { type: string; data: MessageDto | ConversationDto; conversationId?: string };
      if (e.conversationId !== id) return;
      if (e.type === 'chat.message') {
        setMessages((prev) => {
          if (prev.some((m) => m.id === (e.data as MessageDto).id)) return prev;
          return [...prev, e.data as MessageDto];
        });
      } else if (e.type === 'chat.conversation.updated') {
        setConversation(e.data as ConversationDto);
      }
    });
    return unsubscribe;
  }, [id]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !id || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    try {
      await http.post<MessageDto>(`/api/chat/tenant/conversations/${id}/messages`, { content });
    } catch (err) {
      setNewMessage(content);
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async () => {
    if (!id || !conversation) return;
    try {
      await http.post(`/api/chat/tenant/conversations/${id}/read`);
      setConversation((c) => c ? { ...c, unreadTenant: 0 } : null);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (conversation?.unreadTenant > 0) {
      handleMarkRead();
    }
  }, [conversation]);

  if (loading) return <AppLayout brand="LiveStreamX" navGroups={NAV}><SpinnerBlock /></AppLayout>;
  if (!conversation) return <AppLayout brand="LiveStreamX" navGroups={NAV}><div className="p-8 text-center text-muted">Conversation not found</div></AppLayout>;

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/chat')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </Button>
          <div>
            <h1 className="page-title">Support Chat</h1>
            <p className="page-subtitle">{conversation.subject ?? 'General Support'}</p>
          </div>
        </div>
        <Badge tone={conversation.status === 'OPEN' ? 'active' : conversation.status === 'CLOSED' ? 'warning' : 'archived'}>
          {conversation.status}
        </Badge>
      </div>

      <div className="flex flex-col h-[calc(100vh-200px)] min-h-[600px]">
        <Card className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={messagesEndRef}>
            {messages.length === 0 ? (
              <div className="text-center text-muted py-12">No messages yet. Start the conversation!</div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${msg.senderRole === 'TENANT' ? 'justify-end' : ''}`}
                >
                  <Avatar
                    name={msg.senderName ?? (msg.senderRole === 'TENANT' ? 'You' : 'Support')}
                    size="sm"
                    className={msg.senderRole === 'TENANT' ? 'order-2' : ''}
                  />
                  <div
                    className={`max-w-[70%] ${msg.senderRole === 'TENANT' ? 'order-1' : ''}`}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted mb-1">
                      <span className="font-medium">{msg.senderName ?? (msg.senderRole === 'TENANT' ? 'You' : 'Support')}</span>
                      <span>{new Date(msg.createdAt).toLocaleString()}</span>
                    </div>
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        msg.senderRole === 'TENANT'
                          ? 'rounded-br-none'
                          : 'rounded-bl-none'
                      }`}
                      style={{
                        backgroundColor:
                          msg.senderRole === 'TENANT'
                            ? 'var(--primary)'
                            : 'var(--surface-secondary)',
                        color:
                          msg.senderRole === 'TENANT'
                            ? '#fff'
                            : 'var(--text)',
                      }}
                    >
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          {hasMore && (
            <div className="border-t p-3 text-center">
              <Button variant="ghost" size="sm" onClick={() => loadMessages(page + 1, true)} disabled={loading}>
                Load earlier messages
              </Button>
            </div>
          )}
          <div className="border-t p-4">
            <form onSubmit={handleSend} className="flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={conversation.status !== 'OPEN' ? 'Conversation is closed' : 'Type a message…'}
                disabled={conversation.status !== 'OPEN' || sending}
                className="flex-1 resize-none border rounded-lg p-3 min-h-[56px] max-h-48"
                rows={1}
                style={{ backgroundColor: 'var(--surface)', color: 'var(--text)', borderColor: 'var(--border-strong)' }}
              />
              <Button type="submit" disabled={!newMessage.trim() || conversation.status !== 'OPEN' || sending}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}